import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { FormSelect, FormTextField, PageHeader, PasswordField } from '@/components';
import { INDUSTRY_OPTIONS, VENDOR_OPTIONS } from '@/constants/options';
import { STATES, citiesOf, districtsOf } from '@/constants/india';
import { AutocompleteField } from '@/components';
import { brand } from '@/theme/theme';

// ---------------------------------------------------------------------------
// Schema — same as registration but without licence fields
// ---------------------------------------------------------------------------
const schema = z.object({
  tenantName: z.string().min(1, 'Business name is required').max(100),
  industry: z.enum(
    ['PHARMACEUTICAL','FMCG','ELECTRONICS','APPAREL','FOOD_BEVERAGE',
     'HARDWARE','STATIONERY','AUTOMOTIVE','OTHER'],
    { errorMap: () => ({ message: 'Select an industry' }) }
  ),
  gstin: z.string().max(20).optional().or(z.literal('')),
  vendorName: z.string().max(50).optional().or(z.literal('')),
  adminUsername: z
    .string().min(3, 'Min 3 characters').max(100)
    .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, digits, . _ or - only'),
  adminPassword: z.string().min(8, 'Min 8 characters').max(64),
  address: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  district: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(100).optional().or(z.literal('')),
  pincode: z.string().max(10).optional().or(z.literal('')),
  phone: z.string().min(7, 'Enter a valid phone').max(20),
  tenantEmail: z.string().email('Invalid email').optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export function AdminCreateTenantPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      industry: 'PHARMACEUTICAL',
      vendorName: 'NONE',
      adminUsername: '',
      adminPassword: '',
    },
  });

  const watchedState = watch('state') ?? '';

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    setSubmitting(true);
    try {
      await adminApi.createTenant({
        tenantName:    values.tenantName,
        tenantType:    'WHOLESALER',
        industry:      values.industry,
        gstin:         values.gstin,
        vendorName:    values.vendorName === 'NONE' ? undefined : values.vendorName,
        adminUsername: values.adminUsername,
        adminPassword: values.adminPassword,
        address:       values.address,
        city:          values.city,
        district:      values.district,
        state:         values.state,
        pincode:       values.pincode,
        phone:         values.phone,
        tenantEmail:   values.tenantEmail,
      });
      navigate('/admin/tenants', { replace: true });
    } catch (err) {
      setServerError((err as { message?: string })?.message ?? 'Failed to create vendor');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <PageHeader
        title="Create Vendor"
        subtitle="Register a new business on the platform"
      />

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError('')}>
          {serverError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2}>

          {/* Business details */}
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: brand.inkSoft,
              textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
              Business Details
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormTextField name="tenantName" register={register} errors={errors}
              label="Business Name" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect name="industry" control={control} errors={errors}
              label="Industry" options={[...INDUSTRY_OPTIONS]} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField name="phone" register={register} errors={errors}
              label="Phone" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField name="gstin" register={register} errors={errors}
              label="GSTIN (optional)" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect name="vendorName" control={control} errors={errors}
              label="Accounting / ERP software" options={VENDOR_OPTIONS} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField name="tenantEmail" register={register} errors={errors}
              label="Email (optional)" size="small" />
          </Grid>

          {/* Login credentials */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: brand.inkSoft,
              textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
              Admin Login Credentials
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormTextField name="adminUsername" register={register} errors={errors}
              label="Username" autoComplete="off" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PasswordField name="adminPassword" register={register} errors={errors}
              label="Password" autoComplete="new-password" size="small" />
          </Grid>

          {/* Address */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: brand.inkSoft,
              textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
              Address (optional)
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AutocompleteField name="state" control={control} errors={errors}
              label="State" options={STATES} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteField name="district" control={control} errors={errors}
              label="District" options={districtsOf(watchedState)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteField name="city" control={control} errors={errors}
              label="City" options={citiesOf(watchedState)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField name="pincode" register={register} errors={errors}
              label="Pincode" size="small" />
          </Grid>
          <Grid item xs={12}>
            <FormTextField name="address" register={register} errors={errors}
              label="Street / Area" size="small" />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ minWidth: 140 }}
              >
                {submitting ? 'Creating…' : 'Create Vendor'}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </form>
    </Box>
  );
}
